/**
 * ClinicReport  —  /report/:clinicId
 *
 * Top-level clinic dashboard.  Hosts all report tabs (Financial, Monthly Recon,
 * Forecasting, Practitioner Profile, Clinic Profile, Settings, and Schedule).
 *
 * Schedule-tab changes in this branch:
 *   - The "Schedule" tab item now navigates to a dedicated /schedule sub-route
 *     instead of expanding a dropdown of sub-tabs.
 *   - `canSeeScheduleTab` (roles 1-4) is separate from `canSeeClinicAdminTabs`
 *     (roles 1-3) so Practitioner Lead can access Schedule but not Clinic Profile
 *     or Settings.
 *
 * viewPractitioner bug fix:
 *   Previously the selected practitioner object was stored as a state snapshot
 *   (setViewPractitioner(p)).  If practitionersData re-fetched (e.g. after a
 *   schedule change), the Profile tab would still show stale data.  Now only the
 *   ID is stored (`viewPractitionerId`) and `viewPractitioner` is derived via
 *   useMemo over the live practitionersData so it always reflects the latest data.
 *
 * No DB tables are directly accessed from this file — it delegates to child
 * components and hooks that target their own endpoints.
 */
// frontend/src/pages/reporting/ClinicReport.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  CardValue,
  Page,
  Tabs,
  DateStatCard,
  DataStateHandler,
  useReportDate,
  MonthlyClinicSelection,
  ForecastingSummaryBar,
  usePersistedState,
} from "@common";
import {
  FinancialReport,
  PerformanceGlance,
  CollectionBreakdown,
  MonthlyReconReport,
} from "@pages";
import NewPatients from "@pages/reporting/NewPatients";
import DepositSheet from "@pages/admin/DepositSheet";
import Forecasting from "@pages/reporting/Forecasting";
import LabInvoicesTab from "@pages/reporting/LabInvoicesTab";
import ClinicPerformanceReport from "@pages/reporting/ClinicPerformanceReport";
import InvisalignInvoiceReviewPanel from "@pages/admin/InvisalignInvoiceReviewPanel";
import {
  useReportTotal,
  useReportClinicData,
  usePractitioners,
  useCollectionBreakdowns,
  useClinicDetail,
  useForecastsBatch,
} from "@api/hooks";
import ProfileDirectoryPage from "@pages/reporting/ProfileDirectoryPage";
import ProfilePractitionerTab from "./ProfilePractitionerTab";
import ClinicSettings from "./ClinicSettings";
import ClinicVacationTab from "./ClinicVacationTab";
import AdminClinicProfile from "@pages/admin/AdminClinicProfile";
import CashManagement from "@pages/cash_management/CashManagement";
import { useAuth } from "@api/useAuth";
import {
  isClinicScopedUser,
  isOrganizerOrAdmin,
  isManagerOrAbove,
  isViewOnlyRole,
  canRescanLabInvoices,
  canAccessPractitionerDirectory,
  isPractitionerLead,
  torontoDateParts,
  torontoYear,
  torontoMonthIndex,
  isValidYearMonth,
} from "@utils";
import { getRoster } from "@utils/practitionerUtils";

function getPreviousMonthKey() {
  const now = torontoDateParts();
  const month = now.month - 1; // 0-indexed (Jan=0)
  const year = month === 0 ? now.year - 1 : now.year;
  const prev = month === 0 ? 12 : month; // 1-indexed previous month
  return `${year}-${String(prev).padStart(2, "0")}`;
}

function daysInYearMonth(yearMonth) {
  const [year, month] = yearMonth.split("-").map(Number);
  return new Date(year, month, 0).getDate();
}

export default function ClinicReport() {
  const { clinicId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Strip stale query params (e.g. bookmarked ?date=...), but keep report deep-link params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const allowed = new Set([
      "tab",
      "month",
      "date",
      "practitioner",
      "practitioner_id",
      "forecast_month",
      "lab_month",
    ]);
    const hasOther = Array.from(params.keys()).some((k) => !allowed.has(k));
    if (hasOther) {
      const kept = new URLSearchParams();
      for (const k of allowed) {
        const v = params.get(k);
        if (v) kept.set(k, v);
      }
      const search = kept.toString();
      navigate(
        { pathname: window.location.pathname, search: search ? `?${search}` : "" },
        { replace: true },
      );
    }
  }, [navigate]);

  const role = user?.role;

  /*
   * Tab visibility gates — controls which tabs are rendered and which are blocked.
   *
   * canSeeClinicAdminTabs  — true for Organizer, Admin, Area Manager (roles 1-3).
   *   Hides: Clinic Profile and Settings from Practitioner Lead.
   *
   * canSeeScheduleTab — true for roles 1-4 (all managers + Practitioner Lead).
   *   Practitioner Lead can view and use the Schedule section, scoped to their clinic.
   *
   * canSeePractitionerProfileTabs — true for all roles that can view the practitioner directory.
   *   Shows: “Practitioner Profile” list + “Profile” single-person detail tab.
   *   Practitioner Lead CAN see these, but financial rows are hidden inside the child component.
   *
   * canEdit — false for Observer and Bookkeeper (view-only roles).
   *   Passed down to child tabs so they disable create/edit/delete controls.
   */
  const canSeeClinicAdminTabs = isManagerOrAbove(user);
  const canSeeScheduleTab = isManagerOrAbove(user) || isPractitionerLead(user);
  const canSeePractitionerProfileTabs = canAccessPractitionerDirectory(user);
  const canEdit = !isViewOnlyRole(user);

  // Store only the ID — viewPractitioner is derived after practitionersData is declared below
  const [viewPractitionerId, setViewPractitionerId] = useState(null);

  // Daily date (financial tab) — shared across pages
  const { reportDate: date, setReportDate: setDate } = useReportDate();

  useEffect(() => {
    const urlDate = searchParams.get("date");
    if (/^\d{4}-\d{2}-\d{2}$/.test(urlDate || "") && urlDate !== date) {
      setDate(urlDate);
    }
    // Only react to URL changes. User-driven date changes update the URL via
    // handleDateChange; including `date` here would pull the picker back to an
    // old query param before the URL update lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setDate]);

  // Monthly month picker (monthly tab UI). Initial value comes from
  // ?month=YYYY-MM (when deep-linked), else defaults to last completed month.
  const [monthlyMonth, setMonthlyMonth] = useState(() => {
    const m = searchParams.get("month");
    return isValidYearMonth(m) ? m : getPreviousMonthKey();
  });

  // Forecasting tab calendar month — seeded from ?forecast_month=YYYY-MM so refresh restores position.
  const [forecastCalYear, setForecastCalYear] = useState(() => {
    const fromUrl = searchParams.get("forecast_month");
    if (isValidYearMonth(fromUrl)) return Number(fromUrl.split("-")[0]);
    return torontoYear();
  });
  const [forecastCalMonth, setForecastCalMonth] = useState(() => {
    const fromUrl = searchParams.get("forecast_month");
    if (isValidYearMonth(fromUrl)) return Number(fromUrl.split("-")[1]) - 1;
    return torontoMonthIndex();
  });
  const forecastMonthValue = `${forecastCalYear}-${String(forecastCalMonth + 1).padStart(2, "0")}`;

  function handleForecastMonthChange(monthStr) {
    if (!monthStr) return;
    const [yr, mo] = monthStr.split("-").map(Number);
    setForecastCalYear(yr);
    setForecastCalMonth(mo - 1);
  }

  function handleForecastCalNav(year, month) {
    setForecastCalYear(year);
    setForecastCalMonth(month);
  }

  // Month in YYYY-MM for existing daily report total logic
  const monthDate = useMemo(() => date.slice(0, 7), [date]);

  // Data hooks — each returns data + isLoading + error for its own API call
  const {
    data: reportTotalData,
    isLoading: totalsLoading,
    error: totalsError,
  } = useReportTotal({ clinic: clinicId, date: monthDate }, { enablePolling: true });

  const {
    data: reportClinicData,
    isLoading: clinicLoading,
    error: clinicError,
  } = useReportClinicData(clinicId, { date }, { enablePolling: true });

  const {
    data: practitionersData,
    isLoading: practitionersLoading,
    error: practitionersError,
  } = usePractitioners({ clinic: clinicId }, { enablePolling: true });

  // Derived from live practitionersData so Profile tab always reflects the latest
  // schedule/vacation data without needing a page reload.
  const viewPractitioner = useMemo(
    () => getRoster(practitionersData).find((p) => p.id === viewPractitionerId) ?? null,
    [viewPractitionerId, practitionersData],
  );

  const {
    data: collectionBreakdowns,
    isLoading: collectionLoading,
    error: collectionError,
  } = useCollectionBreakdowns(clinicId, { date }, { enablePolling: true });

  const { data: clinicDetail } = useClinicDetail(clinicId);
  const hiddenColumns = clinicDetail?.clinic?.hidden_columns ?? [];

  // Clinic options for the forecast header — scoped to this user's assigned clinics.
  const forecastClinicOptions = useMemo(() => {
    const codes = user?.clinic || [];
    const currentName = (clinicDetail?.clinic?.name || "").trim();
    return codes
      .map((code) => {
        const name = code.toUpperCase() === clinicId?.toUpperCase() ? currentName : "";
        return { value: code, label: name ? `${name} (${code})` : code };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [user?.clinic, clinicId, clinicDetail]);

  // ADDED: persisted per-clinic so the filter survives a page refresh.
  // Key includes clinicId so each clinic page has its own independent selection.
  // Lab vs Invisalign sub-tab under "Lab/Invisalign Invoices"; survives a refresh.
  const [invoiceKind, setInvoiceKind] = usePersistedState("clinicReport:invoiceKind", "lab");
  const [forecastingClinicFilters, setForecastingClinicFilters] = usePersistedState(
    `clinicReport:forecastingClinicFilters:${clinicId}`,
    [],
  );

  // ADDED: empty selection = "All Clinics" — expands to all assigned clinics for fetching/rendering.
  const effectiveForecastingClinics =
    forecastingClinicFilters.length > 0
      ? forecastingClinicFilters
      : forecastClinicOptions.map((o) => o.value);

  // ADDED: date range for the batch fetch, derived from the shared forecast calendar month.
  const forecastBatchMM = String(forecastCalMonth + 1).padStart(2, "0");
  const forecastBatchDateFrom = `${forecastCalYear}-${forecastBatchMM}-01`;
  const forecastBatchDateTo = `${forecastCalYear}-${forecastBatchMM}-${String(
    new Date(forecastCalYear, forecastCalMonth + 1, 0).getDate(),
  ).padStart(2, "0")}`;

  // ADDED: single batch request for all effective clinics instead of individual fetches per block.
  const { data: batchForecastsData } = useForecastsBatch(
    effectiveForecastingClinics,
    { date_from: forecastBatchDateFrom, date_to: forecastBatchDateTo },
    { enabled: effectiveForecastingClinics.length > 0 },
  );

  // Each Forecasting block reports its monthly totals here via onSummaryChange.
  const [forecastClinicSummaries, setForecastClinicSummaries] = useState({});

  const now = torontoDateParts();
  const forecastIsPastMonth =
    forecastCalYear < now.year ||
    (forecastCalYear === now.year && forecastCalMonth < now.month - 1);

  // ADDED: sums mtd/forecast/monthEnd across all rendered clinic blocks for the summary bar.
  const aggregatedForecastSummary = useMemo(() => {
    const active = effectiveForecastingClinics
      .map((c) => forecastClinicSummaries[c])
      .filter(Boolean);
    return {
      mtd: active.reduce((s, x) => s + x.mtd, 0),
      forecastFromToday: active.reduce((s, x) => s + x.forecastFromToday, 0),
      monthEnd: active.reduce((s, x) => s + x.monthEnd, 0),
    };
  }, [forecastClinicSummaries, effectiveForecastingClinics]); // eslint-disable-line react-hooks/exhaustive-deps

  // ADDED: removes summaries for clinics no longer in the effective list to keep totals accurate.
  useEffect(() => {
    setForecastClinicSummaries((prev) => {
      const activeSet = new Set(effectiveForecastingClinics);
      const hasStale = Object.keys(prev).some((k) => !activeSet.has(k));
      if (!hasStale) return prev;
      const next = {};
      for (const c of effectiveForecastingClinics) {
        if (prev[c]) next[c] = prev[c];
      }
      return next;
    });
  }, [effectiveForecastingClinics]); // eslint-disable-line react-hooks/exhaustive-deps

  const [kpis, setKpis] = useState([
    { label: "Total Production Reported", value: "$0.00", icon: "🏠" },
    { label: "Total Collection Reported", value: "$0.00", icon: "🐷" },
  ]);

  const [tab, setTab] = useState(() => searchParams.get("tab") || "financial");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Persist tab in URL so refresh stays on the same tab
  useEffect(() => {
    const current = new URLSearchParams(window.location.search).get("tab");
    if (tab && tab !== current) {
      const next = new URLSearchParams(window.location.search);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Persist monthlyMonth in URL when on monthly tab; clean it up on other tabs
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    const current = next.get("month");
    if (tab === "monthly") {
      if (monthlyMonth && monthlyMonth !== current) {
        next.set("month", monthlyMonth);
        setSearchParams(next, { replace: true });
      }
    } else if (current) {
      next.delete("month");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, monthlyMonth]);

  // Persist forecast month in URL when on forecast tab; clean it up on other tabs
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    const current = next.get("forecast_month");
    if (tab === "forecast") {
      if (forecastMonthValue && forecastMonthValue !== current) {
        next.set("forecast_month", forecastMonthValue);
        setSearchParams(next, { replace: true });
      }
    } else if (current) {
      next.delete("forecast_month");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, forecastMonthValue]);

  // Persist lab invoice month in URL when on lab invoices tab so Pending badges can
  // keep showing All/Delivered/Applied counts for the last interacted month.
  // Deep-link ?lab_month=YYYY-MM seeds the shared date once when entering the tab.
  const seededLabMonthRef = useRef(false);
  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    const urlLabMonth = next.get("lab_month");
    const hasValidUrlLabMonth = isValidYearMonth(urlLabMonth);

    if (tab !== "labInvoices") {
      seededLabMonthRef.current = false;
      if (urlLabMonth) {
        next.delete("lab_month");
        setSearchParams(next, { replace: true });
      }
      return;
    }

    // Drop invalid deep-links like ?lab_month=2025-99 before they can seed state.
    if (urlLabMonth && !hasValidUrlLabMonth) {
      next.delete("lab_month");
      setSearchParams(next, { replace: true });
      return;
    }

    const dateMonth = date.slice(0, 7);
    if (!seededLabMonthRef.current && hasValidUrlLabMonth && urlLabMonth !== dateMonth) {
      seededLabMonthRef.current = true;
      const maxDay = daysInYearMonth(urlLabMonth);
      const day = Math.min(Number(date.slice(8, 10) || "1") || 1, maxDay);
      setDate(`${urlLabMonth}-${String(day).padStart(2, "0")}`);
      return;
    }

    seededLabMonthRef.current = true;
    if (isValidYearMonth(dateMonth) && dateMonth !== urlLabMonth) {
      next.set("lab_month", dateMonth);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, date]);

  // Organizer, Admin, and clinic-scoped roles (Area Manager, PL, Bookkeeper) can set a custom month
  const canOverride = isOrganizerOrAdmin(user) || isClinicScopedUser(user);

  // Safety redirect: if a user deep-links or bookmarks a tab they don't have access to,
  // quietly send them back to the financial tab instead of showing a blank/broken page.
  // We wait until `user` is loaded to avoid resetting a valid tab during the auth bootstrap.
  useEffect(() => {
    if (!user) return;
    const practitionerTabs = ["profile", "profileView"];
    // Include old schedule sub-tab keys so stale bookmarks redirect to financial
    // Clinic Profile + Settings: Organizer/Admin/Area Manager only
    const clinicAdminOnlyTabs = ["clinicProfile", "settings"];
    // Schedule: also visible to Practitioner Lead
    const scheduleTabKeys = [
      "schedule",
      "weeklySchedule",
      "timeOff",
      "clinicVacation",
      "clinicCalendar",
    ];
    if (practitionerTabs.includes(tab) && !canSeePractitionerProfileTabs) setTab("financial");
    if (clinicAdminOnlyTabs.includes(tab) && !canSeeClinicAdminTabs) setTab("financial");
    if (scheduleTabKeys.includes(tab) && !canSeeScheduleTab) setTab("financial");
  }, [tab, user, canSeePractitionerProfileTabs, canSeeClinicAdminTabs, canSeeScheduleTab]);

  const tabItems = useMemo(
    () =>
      [
        { key: "financial", label: "Daily Financial Reporting" },
        { key: "depositSheet", label: "Deposit Sheet" },
        { key: "labInvoices", label: "Lab/Invisalign Invoices" },
        { key: "patients", label: "New Patients" },
        { key: "forecast", label: "Forecasting" },
        { key: "monthly", label: "Monthly Reconciliation" },
        // PL: visible — practitioner roster (ProfileDirectoryPage)
        canSeePractitionerProfileTabs && { key: "profile", label: "Employee Profile" },
        // PL: hidden — clinic-level admin
        canSeeClinicAdminTabs && { key: "clinicProfile", label: "Clinic Profile" },
        // PL: visible when a person selected — read-only detail; financial rows hidden for PL in child
        canSeePractitionerProfileTabs &&
          viewPractitioner && { key: "profileView", label: "Profile" },
        // PL: hidden — clinic settings
        canSeeClinicAdminTabs && { key: "settings", label: "Settings" },
        // Visible to Practitioner Lead too — Schedule page scopes data to their clinic
        canSeeScheduleTab && { key: "schedule", label: "Schedule" },
        // Clinic closures & schedule overrides — same gate as Schedule
        canSeeScheduleTab && { key: "clinicVacation", label: "Clinic Closures" },
        { key: "cashManagement", label: "Cash Management" },
      ].filter(Boolean),
    [canSeePractitionerProfileTabs, canSeeClinicAdminTabs, viewPractitioner, canSeeScheduleTab],
  );

  const KPI_TABS = new Set(["financial", "patients"]);
  const showKpis = KPI_TABS.has(tab);

  // Maps each tab to the subset of fetches it actually needs — avoids blocking tabs on irrelevant loading states
  const tabLoading = useMemo(() => {
    const map = {
      financial: clinicLoading || practitionersLoading || collectionLoading || totalsLoading,
      monthly: clinicLoading || practitionersLoading,
      forecast: totalsLoading,
      patients: practitionersLoading,
      profile: practitionersLoading,
      profileView: practitionersLoading,
    };
    return map[tab] ?? false;
  }, [tab, clinicLoading, practitionersLoading, collectionLoading, totalsLoading]);

  // Clinic Profile uses its own DataStateHandler; don't block on unrelated tab errors
  const dashboardFetchError = clinicError || practitionersError || collectionError || totalsError;
  const tabError = tab === "clinicProfile" ? null : dashboardFetchError;

  // Called from ProfileDirectoryPage when the user clicks "View" on a practitioner row
  function handleViewPractitioner(p) {
    setViewPractitionerId(p.id);
    setTab("profileView");
  }

  function backToProfileTab() {
    setTab("profile");
  }

  const financialPractitionerParam =
    searchParams.get("practitioner") || searchParams.get("practitioner_id") || "";

  function handleDateChange(nextDate) {
    setDate(nextDate);
    const next = new URLSearchParams(window.location.search);
    if (nextDate) {
      next.set("date", nextDate);
    } else {
      next.delete("date");
    }
    setSearchParams(next, { replace: true });
  }

  function handleFinancialPractitionerChange(practitionerId, nextDate = null) {
    const next = new URLSearchParams(window.location.search);
    if (practitionerId) {
      next.set("practitioner", String(practitionerId));
      next.delete("practitioner_id");
    } else {
      next.delete("practitioner");
      next.delete("practitioner_id");
    }
    if (tab !== "financial") {
      next.set("tab", "financial");
    }
    if (nextDate) {
      next.set("date", nextDate);
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <Page>
      {/* Date/Month picker + KPI cards */}
      <div
        // CHANGED: forecast tab was lg:grid-cols-2 (made filter boxes too wide) — now lg:grid-cols-4
        // to match the sizing used on Admin and Management forecast headers.
        className={`grid gap-6 mb-5 ${showKpis ? "md:grid-cols-2 lg:grid-cols-3" : tab === "forecast" ? "md:grid-cols-2 lg:grid-cols-4" : "grid-cols-1"}`}
      >
        {tab === "monthly" ? (
          <DateStatCard
            mode="month"
            label="Month"
            monthValue={monthlyMonth}
            onChangeMonth={setMonthlyMonth}
          />
        ) : tab === "forecast" ? (
          // CHANGED: clinicValue was hardcoded to [clinicId] (always selected, Clear did nothing).
          // Now bound to forecastingClinicFilters state so the user can select, filter, and clear.
          <MonthlyClinicSelection
            months={[forecastMonthValue]}
            onMonthsChange={(months) => handleForecastMonthChange(months[0])}
            disableCurrentAndFuture={false}
            clinicOptions={forecastClinicOptions}
            // CHANGED: was {[clinicId]} — now reflects actual persisted selection
            clinicValue={forecastingClinicFilters}
            onClinicChange={setForecastingClinicFilters}
          />
        ) : (
          <DateStatCard value={date} onChange={handleDateChange} />
        )}

        {/* KPI cards — only shown on financial, patients, and forecast tabs */}
        {showKpis &&
          kpis.map((k, i) => <CardValue key={i} icon={k.icon} label={k.label} value={k.value} />)}
      </div>

      {/* Tabs */}
      <div className="relative mt-2">
        <div className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent z-10" />

        <div
          className="
            -mx4 px-4
            overflow-x-auto
            whitespace-nowrap
            scroll-smooth
            snap-x snap-mandatory
            md:overflow-x-visible
          "
          style={{
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
          }}
        >
          <style>
            {`
              .tabs-scroll::-webkit-scrollbar { height: 6px; }
              .tabs-scroll::-webkit-scrollbar-track { background: transparent; }
              .tabs-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.2); border-radius: 999px; }
            `}
          </style>

          <div className="tabs-scroll min-w-max snap-start pb-2">
            <Tabs
              value={tab}
              onChange={(key) => {
                if (key === "schedule") {
                  navigate(`/report/${clinicId}/schedule`);
                  return;
                }
                setTab(key);
              }}
              items={tabItems}
            />
          </div>
        </div>
      </div>

      {/* Single loading/error wrapper covers all tab content — each tab contributes its own loading state via tabLoading */}
      <DataStateHandler
        isLoading={tabLoading}
        error={tabError}
        isEmpty={false}
        loadingMessage="Loading dashboard..."
        showLoadingSpinner={true}
      >
        {tab === "financial" && (
          <>
            <FinancialReport
              clinicId={clinicId}
              date={date}
              onKpisChange={setKpis}
              clinicData={reportClinicData}
              practitionersData={practitionersData}
              role={role} // so FinancialReport can hide financial rows in practitioner quick look for PL
              hiddenColumns={hiddenColumns}
              canEdit={canEdit}
              initialPractitionerId={financialPractitionerParam}
              onPractitionerChange={handleFinancialPractitionerChange}
            />
            <ClinicPerformanceReport
              clinicId={clinicId}
              date={date}
              clinicData={reportClinicData}
              hiddenColumns={hiddenColumns}
            />
            <div className="mt-6 grid gap-6">
              <CollectionBreakdown
                clinicId={clinicId}
                date={date}
                collectionBreakdowns={collectionBreakdowns}
                clinicData={reportClinicData}
                canEdit={canEdit}
              />
              <PerformanceGlance
                clinicId={clinicId}
                date={date}
                reportTotalData={reportTotalData}
                hiddenColumns={hiddenColumns}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => {
                  setPage(1);
                  setPageSize(n);
                }}
              />
            </div>
          </>
        )}

        {tab === "depositSheet" && <DepositSheet month={monthDate} clinics={[clinicId]} />}

        {tab === "monthly" && (
          <MonthlyReconReport
            clinicId={clinicId}
            month={monthlyMonth}
            canOverrideMonth={canOverride}
            role={role} // used for PL quick-look row hiding (hideFinancialFields) among other monthly rules
            clinicData={reportClinicData}
            practitionersData={practitionersData}
            hiddenColumns={hiddenColumns}
            onOpenDailyDate={(nextDate, practitionerId) => {
              handleDateChange(nextDate);
              setTab("financial");
              handleFinancialPractitionerChange(practitionerId || "", nextDate);
            }}
          />
        )}

        {tab === "patients" && <NewPatients clinicId={clinicId} date={date} canEdit={canEdit} />}

        {/* Renders one Forecasting block per effective clinic (all assigned clinics when
             nothing is selected, or only the chosen subset when a filter is active). */}
        {tab === "forecast" &&
          (effectiveForecastingClinics.length > 0 ? (
            <div className="space-y-6 mt-2">
              <ForecastingSummaryBar
                mtd={aggregatedForecastSummary.mtd}
                forecastFromToday={aggregatedForecastSummary.forecastFromToday}
                monthEnd={aggregatedForecastSummary.monthEnd}
                clinicCount={effectiveForecastingClinics.length}
                isPastMonth={forecastIsPastMonth}
              />
              {effectiveForecastingClinics.map((cId) => {
                const option = forecastClinicOptions.find((o) => o.value === cId);
                return (
                  <Forecasting
                    key={cId}
                    clinicId={cId}
                    hideClinicSelector
                    hideSummary
                    label={option?.label || cId}
                    onSummaryChange={(s) =>
                      setForecastClinicSummaries((prev) => ({ ...prev, [cId]: s }))
                    }
                    calYearOverride={forecastCalYear}
                    calMonthOverride={forecastCalMonth}
                    onCalMonthChange={handleForecastCalNav}
                    forecastsDataOverride={batchForecastsData?.[cId] ?? []}
                  />
                );
              })}
            </div>
          ) : (
            <Forecasting
              clinicId={clinicId}
              date={date}
              reportTotalData={reportTotalData}
              onDateChange={handleDateChange}
              calYearOverride={forecastCalYear}
              calMonthOverride={forecastCalMonth}
              onCalMonthChange={handleForecastCalNav}
            />
          ))}

        {tab === "labInvoices" && (
          <>
            <Tabs
              value={invoiceKind}
              onChange={setInvoiceKind}
              items={[
                { key: "lab", label: "Lab" },
                { key: "invisalign", label: "Invisalign" },
              ]}
            />
            {invoiceKind === "invisalign" ? (
              <InvisalignInvoiceReviewPanel
                key={clinicId}
                clinicId={clinicId}
                defaultMonth={String(date || "").slice(0, 7)}
                canAssign={canEdit}
              />
            ) : (
              <LabInvoicesTab
                clinicId={clinicId}
                defaultDate={date}
                practitionersData={practitionersData}
                canEdit={canEdit}
                canRescan={canRescanLabInvoices(user)}
                hiddenColumns={hiddenColumns}
              />
            )}
          </>
        )}

        {/* Practitioner directory — PL + managers; PL cannot create (see ProfileDirectoryPage). */}
        {tab === "profile" && canSeePractitionerProfileTabs && (
          <div className="mx-auto w-full max-w-5xl px-11 mt-6">
            <ProfileDirectoryPage
              clinicId={clinicId}
              practitionersData={practitionersData}
              role={role} // passed so child can isPractitionerLead(role) for create / modal / fields
              onUpdatePractitioner={null}
              onViewPractitioner={handleViewPractitioner} // opens profileView tab with selected practitioner
            />
          </div>
        )}

        {/* Clinic Profile — not shown to Practitioner Lead (canSeeClinicAdminTabs is false for PL). */}
        {tab === "clinicProfile" && canSeeClinicAdminTabs && (
          <div className="mx-auto w-full max-w-5xl px-4 md:px-11 mt-6">
            <AdminClinicProfile fixedClinicCode={clinicId} />
          </div>
        )}

        {/* Single practitioner page — requirement: PL sees personal/basic only, no compensation block. */}
        {tab === "profileView" && canSeePractitionerProfileTabs && (
          <ProfilePractitionerTab
            practitioner={viewPractitioner}
            onBackToProfile={backToProfileTab}
            // true only for Practitioner Lead — strips insurance / fees / grid / base pay from this view
            hideFinancialFields={isPractitionerLead(user)}
          />
        )}

        {/* Settings — not shown to Practitioner Lead */}
        {tab === "settings" && canSeeClinicAdminTabs && <ClinicSettings clinicId={clinicId} />}

        {/* Clinic Closures & Schedule Overrides */}
        {tab === "clinicVacation" && canSeeScheduleTab && <ClinicVacationTab clinicId={clinicId} />}

        {/* Cash Flow — available to all roles */}
        {tab === "cashManagement" && <CashManagement date={date} clinics={[clinicId]} />}
      </DataStateHandler>
    </Page>
  );
}
